import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@veritrellis/ui";
import { Shield } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader.js";
import { apiFetch } from "../lib/api.js";
import { envLabel } from "../lib/requestPresentation.js";
import { policyModeLabel } from "../lib/policyPresentation.js";
import { usePersistedAdminEnvironment } from "../hooks/usePersistedAdminEnvironment.js";
import { useWorkspaceId } from "../hooks/useWorkspaceId.js";

type Environment = "sandbox" | "production";

type PolicyRow = {
  id: string;
  environment: Environment;
  action_type: string;
  mode: "allow" | "deny" | "require_approval" | "conditional";
  rules: unknown;
  default_decision: string | null;
  auto_allow_low_risk?: boolean;
  pack_id: string | null;
  pack_version: string | null;
  updated_at: string;
};

const modeBadgeVariant = (
  mode: PolicyRow["mode"]
): "success" | "destructive" | "warning" | "default" => {
  if (mode === "allow") return "success";
  if (mode === "deny") return "destructive";
  if (mode === "require_approval") return "warning";
  return "default";
};

const countRules = (rules: unknown): number => (Array.isArray(rules) ? rules.length : 0);

const policySummaryLine = (p: PolicyRow): string => {
  if (p.mode !== "conditional") {
    return policyModeLabel[p.mode];
  }
  const n = countRules(p.rules);
  const def = p.default_decision ?? "deny";
  const auto = p.auto_allow_low_risk === true ? " · auto-allow low risk" : "";
  return `${String(n)} rule${n === 1 ? "" : "s"} · default ${def}${auto}`;
};

export const PoliciesListPage = () => {
  const navigate = useNavigate();
  const { workspaceId, isLoading } = useWorkspaceId();
  const [environment] = usePersistedAdminEnvironment();

  const q = useQuery({
    queryKey: ["policies", workspaceId],
    enabled: workspaceId !== null,
    queryFn: async () => {
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/policies`);
      if (res.status === 403) {
        throw new Error("forbidden");
      }
      if (!res.ok) {
        throw new Error(`policies_${String(res.status)}`);
      }
      return (await res.json()) as { policies: PolicyRow[] };
    }
  });

  if (isLoading) {
    return (
      <div className="admin-page-stack">
        <Skeleton style={{ height: 48, width: "40%" }} />
        <Skeleton style={{ height: 280, width: "100%" }} />
      </div>
    );
  }
  if (workspaceId === null) {
    return <Navigate to="/onboarding" replace />;
  }

  const allPolicies = q.data?.policies ?? [];
  const filtered = allPolicies
    .filter((p) => p.environment === environment)
    .sort((a, b) => a.action_type.localeCompare(b.action_type));

  return (
    <div className="admin-page-stack">
      <PageHeader
        actions={
          <Button asChild>
            <Link to={`/policies/new?env=${environment}`}>New policy</Link>
          </Button>
        }
      />

      {environment === "production" ? (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTitle>Production environment</AlertTitle>
          <AlertDescription>
            Changes here affect live authorizations and are recorded in the product audit log.
          </AlertDescription>
        </Alert>
      ) : null}

      <section className="admin-panel" aria-labelledby="policies-table-heading">
        <div className="admin-panel-header">
          <h2 id="policies-table-heading" className="admin-panel-title">
            {envLabel(environment)} policies
          </h2>
          <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
            {filtered.length} configured
          </span>
        </div>
        <div className="admin-panel-body">
          {q.isLoading ? (
            <div className="admin-panel-body-padded">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} style={{ height: 40, width: "100%", marginBottom: 8 }} />
              ))}
            </div>
          ) : q.isError ? (
            q.error instanceof Error && q.error.message === "forbidden" ? (
              <div className="admin-panel-body-padded">
                <Alert className="border-amber-200 bg-amber-50">
                  <AlertTitle>Insufficient role</AlertTitle>
                  <AlertDescription>
                    Only workspace admins and owners can view policies. Ask an owner to elevate your
                    role.
                  </AlertDescription>
                </Alert>
              </div>
            ) : (
              <div className="admin-panel-body-padded">
                <p style={{ margin: 0, fontSize: 14, color: "var(--destructive)" }}>
                  Failed to load policies.
                </p>
              </div>
            )
          ) : filtered.length === 0 ? (
            <div className="admin-empty-state">
              <div className="admin-empty-state-icon">
                <Shield style={{ width: 22, height: 22 }} />
              </div>
              <p className="admin-empty-state-text">No policies in {envLabel(environment)} yet</p>
              <Button asChild variant="secondary" size="sm">
                <Link to={`/policies/new?env=${environment}`}>Create policy</Link>
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead style={{ paddingLeft: 20 }}>Action</TableHead>
                  <TableHead>Posture</TableHead>
                  <TableHead>Summary</TableHead>
                  <TableHead style={{ textAlign: "right", paddingRight: 20 }}>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow
                    key={p.id}
                    className="admin-table-row-interactive"
                    onClick={() =>
                      navigate(
                        `/policies/${encodeURIComponent(p.action_type)}?env=${p.environment}`
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(
                          `/policies/${encodeURIComponent(p.action_type)}?env=${p.environment}`
                        );
                      }
                    }}
                    tabIndex={0}
                    role="link"
                  >
                    <TableCell style={{ paddingLeft: 20 }}>
                      <span style={{ fontSize: 14, fontWeight: 500 }}>{p.action_type}</span>
                      {p.pack_id != null ? (
                        <Badge variant="outline" style={{ marginLeft: 8, fontSize: 10 }}>
                          pack
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Badge variant={modeBadgeVariant(p.mode)}>{p.mode}</Badge>
                    </TableCell>
                    <TableCell>
                      <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                        {policySummaryLine(p)}
                      </span>
                    </TableCell>
                    <TableCell style={{ textAlign: "right", paddingRight: 20 }}>
                      <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                        {new Date(p.updated_at).toLocaleDateString()}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </section>
    </div>
  );
};
